import React, { useState, useEffect } from 'react';
import 'swiper/css';
import ProductInfoModal from './component/ProductInfoModal';
import ProductCautionModal from './component/ProductCautionModal';
import POSCategoriesSlider from './component/POSCategoriesSlider';
import POSSidebarCard from './component/POSSidebarCard';
import CurrencyCodeAdd from '@/components/common/CurrencyCodeAdd';
import SearchForm from './component/SearchForm';
import POSReceiptModal from './component/POSReceiptModal';
import POSCustomersAddModal from './component/POSCustomersAddModal';
import ToastMe from '@/components/ui/ToastMe';
import validationErrors from '@/lib/validationErrors';
import { getTableCategoryService } from '@/services/ItemCategoryServices';
import { getPosItemService } from '@/services/ItemServices';
import { useSelector, useDispatch } from 'react-redux';
import { getFileUrl } from '@/services/CommonService';
import { addOrderService } from '@/services/OrderServices';
import { getCustomerService } from '@/services/CustomerServices';
import { getPaymentService } from '@/services/PaymentService';
import { removeOrderPOS } from '@/store/actions';
import { getDiningTableService } from '@/services/DiningTableServices';
import { getAdminService } from '@/services/AdminServices';
import { setBranchId, setCompanyId } from '@/security/Axios';
import axios from 'axios';

const WAITER_ROLE_ID = '685b1af7956beac77bebe6d1';

const PosPage = () => {
  const { posOrder } = useSelector((state) => state?.GeneralSetting);
  const dispatch = useDispatch();
  
  // Get branch/company from Redux
  const activeBranchId = useSelector((state) => 
    state?.Branch?.active?._id || 
    state?.Auth?.data?.data?.branch_id || 
    state?.GeneralSetting?.active_branch_id
  );
  const activeCompanyId = useSelector((state) => 
    state?.Auth?.data?.data?.company_id
  );

  const [isLoading, setIsLoading] = useState(false);
  const [receiptOrderId, setReceiptOrderId] = useState('');
  const [categoriesSelectedValue, setCategoriesSelectedValue] = useState(null);
  const [itemSearchValue, setItemSearchValue] = useState('');
  const [dataCategorySource, setDataCategorySource] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [customersOptionList, setCustomersOptionList] = useState([]);
  const [paymentGatewayOptionList, setPaymentGatewayOptionList] = useState([]);
  const [waitersOptionList, setWaitersOptionList] = useState([]);
  const [tables, setTables] = useState([]);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedWaiter, setSelectedWaiter] = useState(null);
  const [selectedPaymentGateway, setSelectedPaymentGateway] = useState(null);
  const [selectedDiscountType, setSelectedDiscountType] = useState(null);
  const [discountValue, setDiscountValue] = useState('');

  const [selectProductData, setSelectProductData] = useState({});
  const [productInfoModalOpen, setProductInfoModalOpen] = useState(false);
  const [selectProductCautionData, setSelectProductCautionData] = useState({});
  const [productCautionModalOpen, setProductCautionModalOpen] = useState(false);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [customersAddModalOpen, setCustomersAddModalOpen] = useState(false);

  const matchesSelectedCategory = (item) => {
    if (!categoriesSelectedValue) return true;
    const selectedId =
      typeof categoriesSelectedValue === 'object'
        ? categoriesSelectedValue.value
        : categoriesSelectedValue;
    if (item.item_category?._id) return item.item_category._id === selectedId;
    if (item.item_category_id) return item.item_category_id === selectedId;
    return false;
  };

  useEffect(() => {
    // Ensure branch and company are set from localStorage/Redux when POS page loads
    const branchId = localStorage.getItem('branch_id');
    const branchName = localStorage.getItem('branch_name');
    const companyId = localStorage.getItem('company_id');

    // Use Redux value if available, otherwise fall back to localStorage
    const effectiveBranchId = activeBranchId || branchId;
    const effectiveCompanyId = activeCompanyId || companyId;

    // Ensure headers are set (this ensures API calls use the correct branch)
    if (effectiveBranchId && effectiveBranchId !== 'all' && effectiveBranchId !== 'null') {
      setBranchId(effectiveBranchId, branchName || undefined);
    }
    if (effectiveCompanyId && effectiveCompanyId !== 'null') {
      setCompanyId(effectiveCompanyId);
    }

    getCategoryData();
    getItemData();
    getCustomers();
    getPaymentGateways();
    getWaiters();
    fetchTables();
    setReceiptOrderId('');
  }, [activeBranchId, activeCompanyId]);

  const fetchTables = () => {
    dispatch(getDiningTableService({ search: '', page: 1, limit: 100 }))
      .then((res) => setTables(res?.data?.data?.data || []))
      .catch(({ message, errorData }) => validationErrors(errorData, message));
  };

  const getCategoryData = () => {
    setIsLoading(true);
    dispatch(getTableCategoryService())
      .then((res) => setDataCategorySource(res?.data?.data || []))
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  };

  const getItemData = () => {
    setIsLoading(true);
    // Call POS items endpoint; show even when out of stock (toggle as needed)
    dispatch(
      getPosItemService({
        search: '',
        page: 1,
        limit: 200,
        in_stock_only: false,
      }),
    )
      .then((res) => {
        const data = res?.data?.data;
        setDataSource(Array.isArray(data) ? data : data?.data || []);
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  };

  const getPaymentGateways = () => {
    setIsLoading(true);
    dispatch(getPaymentService({ search: '', page: 1, limit: 100 }))
      .then((res) => {
        const apiGateways = res?.data?.data?.data || [];
        setPaymentGatewayOptionList(apiGateways.map((g) => ({ value: g._id, label: g.name })));
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  };

  const getCustomers = () => {
    setIsLoading(true);
    dispatch(getCustomerService({ search: '', page: 1, limit: 100 }))
      .then((res) => {
        const list = res?.data?.data?.data || [];
        setCustomersOptionList(list.map((c) => ({ value: c._id, label: c.name })));
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  };

  const getWaiters = () => {
    setIsLoading(true);
    dispatch(getAdminService({ 
      search: '', 
      page: 1, 
      limit: 100,
      extra_filter: { role_id: WAITER_ROLE_ID }
    }))
      .then((res) => {
        const list = res?.data?.data?.data || [];
        setWaitersOptionList(list.map((w) => ({ value: w._id, label: w.name })));
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  };

  const onSelectProductData = (item) => {
    setProductInfoModalOpen(true);
    setSelectProductData(item);
  };

  const onSelectProductCaution = (item) => {
    setProductCautionModalOpen(true);
    setSelectProductCautionData(item);
  };

  const addPosOrder = () => {
    setIsLoading(true);
    if (!posOrder.user_id) return ToastMe('Please select a customer', 'error'), setIsLoading(false);
    if (posOrder.service_mode === 1 && !posOrder.dining_table_id)
      return ToastMe('Please select a table for Dine-In', 'error'), setIsLoading(false);
    if (!posOrder.payment_method_id)
      return ToastMe('Please select a payment method', 'error'), setIsLoading(false);

    // Ensure branch is selected (for takeaway orders)
    const currentBranchId = localStorage.getItem('branch_id') || axios.defaults.headers.common['X-Branch-Id'];
    if (!currentBranchId || currentBranchId === 'all' || currentBranchId === 'null') {
      ToastMe('Please select a branch from the header before creating an order', 'error');
      setIsLoading(false);
      return;
    }

    // Ensure branch header is set (double-check before order creation)
    if (currentBranchId && currentBranchId !== 'all') {
      setBranchId(currentBranchId, localStorage.getItem('branch_name') || undefined);
    }

    const payload = { ...posOrder };
    if (payload.service_mode === 2) delete payload.dining_table_id;

    dispatch(addOrderService(payload))
      .then((res) => {
        setReceiptOrderId(res?.data?.data?._id);
        setReceiptModalOpen(true);
        dispatch(removeOrderPOS());
        setSelectedCustomer(null);
        setSelectedWaiter(null);
        setSelectedPaymentGateway(null);
        setSelectedDiscountType(null);
        setDiscountValue('');
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      <div className="md:w-[calc(100%-340px)] lg:w-[calc(100%-320px)] xl:w-[calc(100%-377px)]">
        <SearchForm itemSearchValue={itemSearchValue} setItemSearchValue={setItemSearchValue} />
        <POSCategoriesSlider
          dataCategorySource={dataCategorySource}
          categoriesSelectedValue={categoriesSelectedValue}
          setCategoriesSelectedValue={setCategoriesSelectedValue}
        />
        <div className="grid gap-3 sm:gap-[18px] grid-cols-[repeat(auto-fill,_minmax(140px,_1fr))] sm:grid-cols-[repeat(auto-fill,_minmax(185px,_1fr))] mb-8 md:mb-0">
          {dataSource
            .filter(matchesSelectedCategory)
            .filter((i) =>
              itemSearchValue
                ? i.name?.toLowerCase().includes(itemSearchValue.toLowerCase())
                : true,
            )
            .map((item) => {
              const displayPrice =
                item?.effective_price ??
                (item.price || 0) -
                  (((item.price || 0) * (item.offer?.discount_per || 0)) / 100);

              return (
                <div
                  key={item._id}
                  className="pos-card-height rounded-2xl border transition border-[#EFF0F6] bg-white hover:shadow-xs"
                >
                  <img
                    className="pos-image-height w-full rounded-t-2xl"
                    src={getFileUrl(item.file_name, 'product')}
                    alt={item.name}
                  />
                  <div className="py-3 px-3 rounded-b-2xl">
                    <h3 className="text-sm mb-3 font-medium font-rubik capitalize overflow-hidden">
                      {item.name}
                    </h3>
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="font-rubik">
                        <CurrencyCodeAdd price={displayPrice} />
                      </h4>
                      <button
                        onClick={() => onSelectProductData(item)}
                        className="db-product-cart pos-add-button flex items-center gap-1.5 rounded-3xl text-sm font-medium py-1 px-2 shadow-cardCart bg-white hover:bg-primary"
                      >
                        <i className="lab lab-bag-2 lab-font-size-14 text-primary" />
                        <span className="text-xs font-rubik text-primary">Add</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>

        <ProductInfoModal
          modalOpen={productInfoModalOpen}
          setModalOpen={setProductInfoModalOpen}
          handleProductCaution={onSelectProductCaution}
          selectProductData={selectProductData}
          setSelectProductData={setSelectProductData}
        />

        <ProductCautionModal
          modelTitle={selectProductCautionData?.name}
          itemDescription={selectProductCautionData?.caution}
          modalOpen={productCautionModalOpen}
          setModalOpen={setProductCautionModalOpen}
        />
      </div>

      <POSSidebarCard
        tables={tables}
        customersAddModalEvent={setCustomersAddModalOpen}
        customersOptionList={customersOptionList}
        waitersOptionList={waitersOptionList}
        paymentGatewayOptionList={paymentGatewayOptionList}
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
        selectedWaiter={selectedWaiter}
        setSelectedWaiter={setSelectedWaiter}
        selectedPaymentGateway={selectedPaymentGateway}
        setSelectedPaymentGateway={setSelectedPaymentGateway}
        selectedDiscountType={selectedDiscountType}
        setSelectedDiscountType={setSelectedDiscountType}
        discountValue={discountValue}
        setDiscountValue={setDiscountValue}
        addPosOrder={addPosOrder}
        isLoading={isLoading}
      />

      <POSReceiptModal
        modalOpen={receiptModalOpen}
        setModalOpen={setReceiptModalOpen}
        receiptOrderId={receiptOrderId}
      />

      <POSCustomersAddModal
        modalOpen={customersAddModalOpen}
        setModalOpen={setCustomersAddModalOpen}
        getCustomersData={getCustomers}
      />
    </>
  );
};

export default PosPage;
